import React from 'react'

import { Select } from '../../../common/components/select'
import { ExplicitChangeTextInput } from '../../../common/components/explicit-change-text-input'
import { BlurChangeTextarea } from '../../../common/components/blur-change-textarea'
import { usePageEditorContext } from '../../contexts/page-editor.context'
import { Accordion, AccordionSummary, AccordionDetails } from '../../editor/sidebar-accordion'
import { ManualBlockEditor } from '../../editor/manual-block-editor'
import { CategoryListEdit } from '../category-list-edit'
import { formatOptionsService } from '../format-options.service'
import { reportDataSetService } from '../../editor/report-data-set.service'
import { HeaderEditAccordion } from '../header-edit-accordion'
import { FooterEditAccordion } from '../footer-edit-accordion'
import { FilterEditAccordion } from '../filter-edit-accordion'
import { SplitChartFilterEditAccordion } from '../split-chart-filter-edit-accordion'

const GROUP_MODE = [
  { value: 'grouped', label: 'Grouped' },
  { value: 'stacked', label: 'Stacked' }
]

export const BarBlockEditSidebar = function ({ block }) {
  const pageEditorContext = usePageEditorContext() || {}

  const strataLegendLabels = reportDataSetService.getStrataLegendLabels(block)
  const filterOptionLabels = reportDataSetService.getFilterOptionLabels(block)

  function updateBlock (block) {
    pageEditorContext.registerUnsavedChanges()
    pageEditorContext.updateCurrentEdit({ block: { ...block } })
  }

  function getBlockProp (prop, defaultValue) {
    if (block.props[prop] === undefined) {
      return defaultValue
    } else {
      return block.props[prop]
    }
  }

  function setBlockProp (prop, value) {
    block.props[prop] = value
    updateBlock(block)
  }

  function getFormatValue (prop) {
    let value = block.props[prop]
    return formatOptionsService.getSelectedFormatOption(prop, value)
  }

  function setFormatValue (prop, value) {
    block.props[prop] = value
    updateBlock(block)
  }

  function getAxisLabels (prop) {
    const labels = getBlockProp('axisLabels', {})
    if (prop) {
      return labels[prop]
    }
    return labels
  }

  function setAxisLabels (prop, value) {
    const axisLabels = getAxisLabels()
    axisLabels[prop] = value
    setBlockProp('axisLabels', axisLabels)
  }

  function getTickValues (prop) {
    const values = getBlockProp(prop, { x: [] })
    return values && Array.isArray(values.x) ? values.x.join('\n') : ''
  }

  function setTickValues (prop, value) {
    value = value.split('\n').filter((x) => x.trim().length > 0)
    // if there are no tick values, remove the prop altogether (otherwise we'd have no ticks on the axis)
    if (value.length === 0) {
      value = undefined
    }
    setBlockProp(prop, { x: value })
  }

  function getDomainValues (prop, index) {
    let domainValues = getBlockProp(prop, []) || []
    if (index !== undefined) {
      return isEmpty(domainValues[index]) ? '' : domainValues[index].toString()
    }
    return domainValues
  }

  function setDomainValues (prop, index, value) {
    // Convert domain value if needed
    if (!isEmpty(value) && (
      (prop === 'yValueDomain' && getBlockProp('yFormat') !== 'date')
    )) {
      value = parseFloat(value)
    }

    let valueDomain = getDomainValues(prop)
    valueDomain[index] = value
    if (isEmpty(valueDomain[0]) && isEmpty(valueDomain[1])) {
      valueDomain = undefined
    } else if (isEmpty(valueDomain[0])) {
      valueDomain = [null, valueDomain[1]]
    } else if (isEmpty(valueDomain[1])) {
      valueDomain = [valueDomain[0]]
    }
    setBlockProp(prop, valueDomain)
  }

  function isEmpty (value) {
    return value === undefined || value === null || value === ''
  }

  function buildFormatField (className, prop, label) {
    return (
      <div className={className} data-qa={`full-width-field-${label}`}>
        <Select
          label={label}
          options={formatOptionsService.getLineBarMapFormatOptions(prop)}
          size="medium"
          value={getFormatValue(prop)}
          isDisabled={false}
          onChange={({ value }) => {
            setFormatValue(prop, value)
          }}
        />
      </div>
    )
  }

  return <div className="report-editor-sidebar">
    <Accordion square defaultExpanded={true}>
      <AccordionSummary>
        Format
      </AccordionSummary>
      <AccordionDetails>
        <div className="field-grid">
          {buildFormatField('full-width-field', 'yFormat', 'Y Format')}
          <div className="full-width-field" data-qa="full-width-field-type">
            <Select
              label="Type"
              options={GROUP_MODE}
              size="medium"
              value={GROUP_MODE.find((mode) => (mode.value === getBlockProp('groupMode')))}
              onChange={({ value }) => {
                setBlockProp('groupMode', value)
              }}
            />
          </div>
        </div>
      </AccordionDetails>
    </Accordion>
    <Accordion square>
      <div data-qa='accordion-summary-axis'>
        <AccordionSummary>
          Axis
        </AccordionSummary>
      </div>
      <AccordionDetails>
        <div className="field-grid">
          <div className="full-width-field">
            <ExplicitChangeTextInput
              size="medium"
              label="X Axis Label"
              value={getAxisLabels('x')}
              onChange={(event) => {
                setAxisLabels('x', event.target.value)
              }}
            />
          </div>
          <div className="full-width-field">
            <ExplicitChangeTextInput
              size="medium"
              label="Y Axis Label"
              value={getAxisLabels('y')}
              onChange={(event) => {
                setAxisLabels('y', event.target.value)
              }}
            />
          </div>
          {[
            ['yValueDomain', 'Y Axis Min', 0],
            ['yValueDomain', 'Y Axis Max', 1]
          ].map(([prop, label, domainIndex]) => (
            <div key={`${prop}-${domainIndex}`} className="field">
              <ExplicitChangeTextInput
                size="medium"
                label={label}
                value={getDomainValues(prop, domainIndex)}
                onChange={(event) => {
                  setDomainValues(prop, domainIndex, event.target.value)
                }}
              />
            </div>
          ))}
          <div className="full-width-field">
            <BlurChangeTextarea
              size="medium"
              rows={3}
              label="X Axis Tick Values"
              placeholder="Put each value on a new line"
              value={getTickValues('tickValues')}
              onChange={(event) => {
                setTickValues('tickValues', event.target.value)
              }}
            />
          </div>
        </div>
      </AccordionDetails>
    </Accordion>
    {strataLegendLabels.filter(({ values }) => values.length > 0).length > 0 && (
      <Accordion square>
        <AccordionSummary>
        Legend
        </AccordionSummary>
        <AccordionDetails>
          <div className="field-grid">
            <div className="full-width-field">
              <CategoryListEdit
                size="medium"
                categoryLabel="Stratify By"
                listLabel="Legend Labels"
                categories={reportDataSetService.getStrataLegendLabels(block)}
                hideLabelToggles={true}
                onChange={(updatedSubgroups) => {
                  block = reportDataSetService.updateStrataLegendLabels(block, updatedSubgroups)
                  updateBlock(block)
                }}
              />
            </div>
          </div>
        </AccordionDetails>
      </Accordion>
    )}
    {filterOptionLabels.filter(({ values, isSplitChartFilter }) => !isSplitChartFilter && values.length > 0).length > 0 && (
      <FilterEditAccordion block={block} />
    )}
    <SplitChartFilterEditAccordion block={block} />
    <HeaderEditAccordion block={block} />
    <FooterEditAccordion block={block} />
    <Accordion square>
      <AccordionSummary>
        Other
      </AccordionSummary>
      <AccordionDetails>
        <div className="field-grid">
          <div className="full-width-field">
            <ManualBlockEditor block={block} onChange={updateBlock} />
          </div>
        </div>
      </AccordionDetails>
    </Accordion>
  </div>
}
